import { DateTime } from 'luxon'
import React from 'react'
import * as Avatar from '../Avatar'
import * as Content from '../Content'
import { Title } from './Title'
import { Body } from './Body'
import { View, CardViewProps } from './View'
import { concatStyles, truncate } from 'utils'
import { useState } from 'react'
import { useCallback } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../core/tooltip'

export type EventStatus = 'CANCELLED' | 'DRAFT' | 'PUBLISHED'

const ARCHIVE_STYLES =
  'absolute top-0 right-0 -mr-2 -mt-2 rounded-full text-white bg-metricbackground px-4 py-1 text-xs font-bold shadow z-20'
const DISABLED_STYLES = 'opacity-25 pointer-events-none'

export interface EventCardProps extends CardViewProps {
  /**
   * Details of the event to render on the card.
   */
  event: any // this was Event as EventSchema
  /**
   * Adjusts the layout of the status bubble in relation to the card titles.
   */
  vertical?: boolean

  /**
   * An event handler that is called when the user chooses to archive the
   * card.
   */
  onArchive?: (event: any) => Promise<void>

  /**
   * Whether the card should be shown in a disabled state with reduced opacity.
   */
  disabled?: boolean

  /**
   * Tooltip message to show when the card is disabled and user hovers over it.
   */
  disabledMessage?: string
}

export const Event: React.FC<EventCardProps> = ({
  event,
  vertical,
  onArchive,
  className,
  disabled = false,
  disabledMessage,
  ...view
}) => {
  // Extract dates from the nested structure using correct approach
  const eventDates = event?.dates

  let dateDisplay = 'Dates TBD'

  // Handle dates properly by combining dates and times arrays
  if (eventDates?.dates && eventDates.dates.length > 0) {
    const firstDateEntry = eventDates.dates[0]
    const firstTimeEntry = eventDates.times?.[0]

    if (firstTimeEntry && firstTimeEntry.start && firstTimeEntry.end) {
      // Combine date from dates array with time from times array
      const startDateStr = `${firstDateEntry.startDate?.split('T')[0]}T${
        firstTimeEntry.start
      }:00`
      const endDateStr = `${
        (firstDateEntry.endDate ?? firstDateEntry.startDate)?.split('T')[0]
      }T${firstTimeEntry.end}:00`

      dateDisplay = `${DateTime.fromISO(startDateStr).toFormat(
        'MMM, d, yyyy'
      )} • ${
        // Format start time without minutes if on the hour
        DateTime.fromISO(startDateStr).toFormat(
          DateTime.fromISO(startDateStr).minute === 0 ? 'ha' : 'h:mma'
        )
      }${
        // Only show end time if different from start time
        firstTimeEntry.start !== firstTimeEntry.end
          ? ` - ${DateTime.fromISO(endDateStr).toFormat(
              DateTime.fromISO(endDateStr).minute === 0 ? 'ha' : 'h:mma'
            )}`
          : ''
      }`
    } else if (firstDateEntry) {
      // No times, just show the date
      dateDisplay = DateTime.fromISO(firstDateEntry.startDate).toFormat(
        'MMM, d, yyyy'
      )
    }
  }

  const colorForTag = () => {
    switch (event.status as EventStatus) {
      case 'CANCELLED':
        return 'destructive'
      case 'DRAFT':
        return 'warning'
      default:
        return undefined
    }
  }
  const [storedTimeout, setStoredTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null)

  const [hovering, setHovering] = useState(false)
  const handleHover = useCallback(
    (newHover: boolean) => () => {
      if (storedTimeout) {
        clearTimeout(storedTimeout)
      }
      if (!newHover) {
        setStoredTimeout(
          setTimeout(() => {
            setHovering(newHover)
          }, 100)
        )
      } else {
        setHovering(newHover)
      }
    },
    [hovering, setHovering, setStoredTimeout, storedTimeout]
  )
  const [archiving, setArchiving] = useState(false)
  const handleArchive: React.MouseEventHandler = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setArchiving(true)
    await onArchive?.(event)
    setArchiving(false)
  }

  const handleClick: React.MouseEventHandler = (e) => {
    if (disabled) {
      // Let the click bubble up to parent even when disabled
      // Parent can handle navigation to event detail page
      return
    }
    // Normal click handling
    view.onClick?.(e)
  }

  const cardContent = (
    <View
      {...view}
      onClick={handleClick}
      onMouseOut={handleHover(false)}
      onMouseOver={handleHover(true)}
      className={concatStyles(
        archiving && DISABLED_STYLES,
        disabled && 'opacity-50 cursor-pointer',
        className
      )}
    >
      <Avatar.Simple
        name={event.name}
        maxInitials={1}
        color={event.color as string}
        imageUrl={event.iconUrl as string}
        className="mr-2 my-auto"
      />
      <div className="flex-col my-auto flex-grow">
        <div className={vertical ? 'flex flex-col' : 'flex flex-row'}>
          {(event?.status as EventStatus) !== 'PUBLISHED' ? (
            <p
              className={
                vertical
                  ? 'flex mr-auto mb-1'
                  : 'absolute bottom-0 right-0 -mb-2 -mr-2'
              }
            >
              <Content.Bubble
                bubbleStyle={colorForTag()}
                className="rounded-sm"
              >
                {event.status}
              </Content.Bubble>
            </p>
          ) : null}
          <Title className="truncate">
            {truncate(event.name, vertical ? 24 : 120)}
          </Title>
        </div>
        <Body>{dateDisplay}</Body>
        {onArchive && hovering && !archiving ? (
          <button
            className={ARCHIVE_STYLES}
            onMouseOver={handleHover(true)}
            onClick={handleArchive}
          >
            Archive
          </button>
        ) : (
          <span className="hidden" />
        )}
      </div>
    </View>
  )

  // Wrap in tooltip if disabled with message
  if (disabled && disabledMessage) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
          <TooltipContent>
            <p>{disabledMessage}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return cardContent
}

Event.displayName = 'Card.Event'
